import { useEffect, useMemo, useRef } from "react"
import {
  Heart, Copy, Flame, ArrowUpRight,
  Zap, Wrench, GitBranch, Bot, Users, MessageCircle,
} from "lucide-react"
import { cn } from "@/lib/utils"
import type { CommunityPost } from "@/lib/community/types"
import type { AbilityType } from "@/lib/community/types"
import { ABILITY_TYPE_CONFIG } from "@/lib/community/types"
import { useFeed } from "@/lib/community/hooks/use-feed"
import { stripMarkdown } from "@/lib/community/utils"

const TYPE_ICONS: Record<string, typeof Zap> = {
  Zap, Wrench, GitBranch, Bot, Users, MessageCircle,
}

interface ProfilePostsSectionProps {
  userId: string
  onPostClick: (post: CommunityPost) => void
  onViewAll?: () => void
}

function PostCard({
  post,
  index,
  onClick,
}: {
  post: CommunityPost
  index: number
  onClick: () => void
}) {
  const config = ABILITY_TYPE_CONFIG[post.abilityType]
  const IconComp = TYPE_ICONS[config.icon] ?? Zap
  const isLarge = index === 0

  return (
    <article
      onClick={onClick}
      className={cn(
        "group relative overflow-hidden rounded-xl liquid-glass-card cursor-pointer transition-all duration-500 hover:border-primary/50",
        isLarge ? "profile-bento-hero" : "",
      )}
    >
      {/* Thumbnail */}
      <div className="relative w-full overflow-hidden bg-secondary aspect-video">
        <img
          src={post.coverImage || `${import.meta.env.BASE_URL}images/profile/default-post-cover.png`}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        {/* Hover gradient overlay */}
        <div className="absolute inset-0 profile-post-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Hot badge */}
        {post.stats.copies > 1000 && (
          <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-full">
            <Flame className="w-3 h-3" />
            HOT
          </div>
        )}

        {/* Type label */}
        <div className={cn(
          "absolute top-4 right-4 font-mono text-xs tracking-widest",
          config.outlineTextClass,
        )}>
          {config.label.toUpperCase()}
        </div>

        {/* Hover arrow */}
        <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <ArrowUpRight className="w-5 h-5 text-primary-foreground" />
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col gap-2">
        <h3 className="font-medium text-sm leading-snug text-foreground line-clamp-2 transition-colors group-hover:text-primary">
          {post.title}
        </h3>

        <p className={cn(
          "text-xs text-muted-foreground leading-relaxed",
          isLarge ? "text-sm line-clamp-3" : "text-xs line-clamp-2",
        )}>
          {stripMarkdown(post.description)}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 text-muted-foreground">
          <span className="flex items-center gap-1.5 text-xs">
            <Heart className="w-3.5 h-3.5" />
            {post.stats.likes.toLocaleString()}
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <Copy className="w-3.5 h-3.5" />
            {post.stats.copies.toLocaleString()}
          </span>
        </div>
      </div>
    </article>
  )
}

/**
 * Hotness score — time-decay weighted engagement.
 *
 * Formula:  score = weighted_engagement / (age_hours + 2) ^ 1.5
 *
 * Weights: copies ×5, tips ×3, likes ×2, comments ×1
 * The +2 offset prevents division-by-zero on fresh posts.
 * The 1.5 exponent gives moderate decay — a post from 24h ago
 * scores ~12× lower than an equally-engaged post from just now.
 */
function computeHotScore(post: CommunityPost): number {
  const { likes, copies, comments, tips } = post.stats
  const engagement = likes * 2 + copies * 5 + comments * 1 + tips * 3
  // age in hours since creation
  const ageMs = Date.now() - new Date(post.createdAt).getTime()
  const ageHours = Math.max(0, ageMs / (1000 * 60 * 60))
  return engagement / Math.pow(ageHours + 2, 1.5)
}

export function ProfilePostsSection({ userId, onPostClick, onViewAll }: ProfilePostsSectionProps) {
  const { posts, loading, hasMore, loadMore } = useFeed({ userId })

  // Keep loading more pages (up to 5) to get a comprehensive hot-sort sample
  const pagesLoadedRef = useRef(1)
  useEffect(() => {
    pagesLoadedRef.current = 1
  }, [userId])

  useEffect(() => {
    if (!loading && hasMore && pagesLoadedRef.current < 5) {
      pagesLoadedRef.current += 1
      loadMore()
    }
  }, [loading, hasMore, loadMore])

  const hotPosts = useMemo(() => {
    const sorted = [...posts].sort((a, b) => computeHotScore(b) - computeHotScore(a))
    return sorted.slice(0, 5)
  }, [posts])

  if (loading && posts.length === 0) {
    return (
      <section className="profile-section">
        <div>
          <div className="profile-section-header">
            <div className="h-5 w-20 bg-muted/50 rounded animate-pulse" />
          </div>
          <div className="profile-bento-grid">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-xl bg-muted/30 animate-pulse",
                  i === 0 ? "profile-bento-hero" : "",
                )}
                style={{ minHeight: i === 0 ? 280 : 140 }}
              />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="profile-section">
      <div>
        {/* Section header */}
        <div className="profile-section-header">
          <h2 className="profile-section-title">热门作品</h2>
          {onViewAll && hotPosts.length > 0 && (
            <button
              type="button"
              onClick={onViewAll}
              className="profile-section-text-action hidden md:flex group"
            >
              <span>查看全部</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          )}
        </div>

        {hotPosts.length === 0 ? (
          <div className="profile-empty-state">
            <Flame className="h-10 w-10 profile-empty-state-icon" />
            <p className="profile-empty-state-title">还没有发布作品</p>
          </div>
        ) : (
          <>
            {/* Bento Grid — max 5 items, compact 2-row layout */}
            <div className="profile-bento-grid">
              {hotPosts.map((post, index) => (
                <PostCard
                  key={post.id}
                  post={post}
                  index={index}
                  onClick={() => onPostClick(post)}
                />
              ))}
            </div>

            {/* Mobile "view all" */}
            {onViewAll && (
              <div className="mt-8 md:hidden flex justify-center">
                <button
                  type="button"
                  onClick={onViewAll}
                  className="px-8 py-3 border border-border text-foreground font-medium rounded-full hover:bg-secondary transition-colors"
                >
                  查看全部作品
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
